import axios from 'axios';
import { getApiBaseUrl, getApiHostHeaders, isApiConfigured } from '@/lib/api-url';
import { trackRegisterEvent } from '@/lib/register-analytics';
import { trackCheckoutEvent } from '@/lib/checkout-analytics';
import { trackSubscriptionEvent } from '@/lib/subscription-analytics';
import { emitMemberDisabled } from '@/lib/session-events';
import { getActingProviderId } from '@/store/actingProviderStore';
import { showPlanFeatureFromError } from '@/store/planFeatureStore';
import { getApiErrorCode } from '@/utils/api-error';
import { CAPTCHA_HEADER, getCaptchaToken, needsCaptcha } from '@/lib/captcha/captcha-token';

const SERVER_ERROR_MESSAGE = 'Algo deu errado do nosso lado. Tente novamente em instantes.';

const api = axios.create({
  baseURL: getApiBaseUrl() ?? '',
});

api.interceptors.request.use((config) => {
  if (!isApiConfigured()) {
    return Promise.reject(new Error('API_NOT_CONFIGURED'));
  }
  for (const [key, value] of Object.entries(getApiHostHeaders())) {
    config.headers.set(key, value);
  }
  // Dono vendo outro profissional: a API confere o vínculo e responde como ele.
  // Header já posto na chamada (marcar para outro sem trocar o foco) prevalece.
  const actingProviderId = getActingProviderId();
  if (actingProviderId && !config.headers.has('X-Acting-Provider')) {
    config.headers.set('X-Acting-Provider', actingProviderId);
  }
  return config;
});

// Rotas públicas com captcha na API: token novo a cada chamada (uso único).
api.interceptors.request.use(async (config) => {
  if (needsCaptcha(config.method, config.url) && !config.headers.has(CAPTCHA_HEADER)) {
    const token = await getCaptchaToken();
    if (token) config.headers.set(CAPTCHA_HEADER, token);
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url ?? '';

    // O dono desativou este membro: quem cuida da sessão refaz o /user/me.
    if (status === 403 && getApiErrorCode(error) === 'MEMBER_DISABLED') {
      emitMemberDisabled();
    }

    // Recurso pago no plano gratuito: o aviso de "assine para liberar" abre
    // daqui, para toda tela, mesmo a que não sabe de plano.
    if (status === 403) showPlanFeatureFromError(error);

    const isNetworkError = !error?.response;
    const is5xx = status >= 500;
    const is429 = status === 429;

    if (isNetworkError || is5xx || is429) {
      const errorMessage = isNetworkError ? error?.message || 'Network Error' : `HTTP ${status}`;

      if (url.includes('/charge/') && url.includes('/public')) {
        const chargeId = url.match(/\/charge\/([^/]+)\/public/)?.[1];
        trackCheckoutEvent({
          event: 'checkout_load_error',
          chargeId,
          errorMessage,
          httpStatus: status,
        });
      } else if (url.includes('/stripe/create-checkout-session')) {
        trackSubscriptionEvent({
          event: 'subscription_create_session_error',
          errorMessage,
          httpStatus: status,
        });
      } else if (url.includes('/auth') || url.includes('/register')) {
        trackRegisterEvent({
          event: 'register_api_error',
          errorMessage,
          httpStatus: status,
        });
      }
    }

    // 5xx nunca mostra o motivo real (SQL, coluna, stack) em tela nenhuma:
    // troca a mensagem na origem, antes de qualquer `err.message` ou
    // `response.data.message` chegar a um toast.
    if (is5xx) {
      if (error.response.data && typeof error.response.data === 'object') {
        error.response.data.message = SERVER_ERROR_MESSAGE;
      } else {
        error.response.data = { statusCode: status, message: SERVER_ERROR_MESSAGE };
      }
      error.message = SERVER_ERROR_MESSAGE;
    }

    return Promise.reject(error);
  },
);

export default api;
